import React, {useEffect, useReducer} from 'react'
import Text from '../../../components/text/Text'
import UnderLineInput from '../../../components/input/UnderLineInput'
import DateInput from '../../../components/input/DateInput'
import WizButton from '../../../components/button/WizButton'
import WizSelectButton from '../../../components/button/WizSelectButton'
import {IPromotions} from '../../../apis/promotion/list'
import {useTime} from '../../../utils/time'
import {createPromotion} from '../../../apis/promotion/create'
import {editPromotion} from '../../../apis/promotion/edit'
import {deletePromotion} from '../../../apis/promotion/delete'

interface IProps {
    viewMode: 'create' | 'edit'
    promotionData?: IPromotions | null
    onSuccess: (updatedData?: IPromotions) => void
    onDelete?: () => void
}

const initState = {
    end_at: '',
    language: 'en',
    name: '',
    priority: 0,
    start_at: '',
    status: 'draft',
    pc_url: '',
    mo_url: '',
}

const languageList = [
    {id: 'en', value: 'English (EN)'},
    {id: 'ko', value: 'Korean (KO)'},
]

export function PromotionDetailsForm({viewMode, promotionData, onSuccess, onDelete}: IProps) {
    const {convertTimezoneToInput, convertInputToTimezone} = useTime()

    const [state, dispatch] = useReducer(reducer, initState)
    const {end_at, language, name, priority, start_at, status, pc_url, mo_url} = state

    function reducer(state, action) {
        switch (action.type) {
            case 'field_create':
                return {
                    end_at: undefined,
                    language: localStorage.getItem('lang') || 'en',
                    name: '',
                    priority: 0,
                    start_at: undefined,
                    status: 'active',
                    pc_url: '',
                    mo_url: '',
                }
            case 'field_reset':
                return {
                    end_at: (convertTimezoneToInput(promotionData?.end_at, 9) as string) || undefined,
                    language: promotionData?.language || localStorage.getItem('lang') || 'en',
                    name: promotionData?.name,
                    priority: promotionData?.priority,
                    start_at: (convertTimezoneToInput(promotionData?.start_at, 9) as string) || undefined,
                    status: promotionData?.status,
                    pc_url: promotionData?.pc_url || '',
                    mo_url: promotionData?.mo_url || '',
                }
            case 'field_update':
                return {...state, [action.field]: action.value}
        }
        return {...state}
    }

    useEffect(() => {
        if (viewMode === 'edit' && promotionData) {
            dispatch({type: 'field_reset'})
        } else if (viewMode === 'create') {
            dispatch({type: 'field_create'})
        }
    }, [promotionData, viewMode])

    const handleDelete = async () => {
        if (!promotionData?.id) return
        if (window.confirm('Are you sure you want to delete this promotion?')) {
            const result = await deletePromotion(promotionData.id)
            if (result?.status === 200 || result?.data?.success) {
                alert('Promotion Deleted Successfully')
                if (onDelete) onDelete()
            } else {
                alert('Delete Promotion Failed')
            }
        }
    }

    const handleSubmit = async () => {
        if (isNaN(new Date(start_at).getTime()) || isNaN(new Date(end_at).getTime())) {
            alert('Please check date format')
            return
        }
        if (!name) {
            alert('Please fill name fields')
            return
        }

        const payload = {
            name,
            status: promotionData?.status || 'active',
            start_at: (convertInputToTimezone(start_at, 9) as string) || null,
            end_at: (convertInputToTimezone(end_at, 9) as string) || null,
            priority: Number(priority),
            language,
            pc_url,
            mo_url,
        }

        if (viewMode === 'edit') {
            const result = await editPromotion(promotionData?.id, {...payload, id: promotionData?.id})
            if (result?.data?.success) {
                alert('Promotion Saved Successfully')
                onSuccess()
            } else {
                alert('Edit Promotion Failed')
            }
        } else {
            const result = await createPromotion(payload)
            if (result?.data?.id || result?.data?.success) {
                alert('Promotion Created Successfully')
                onSuccess(result?.data?.data)
            } else {
                alert('Create Promotion Failed')
            }
        }
    }

    return (
        <div className="bg-gray-50/50 p-4 rounded-lg">
            {/* Language Indicator Mode */}
            <div className="flex items-center gap-2 mb-6">
                <span
                    className={`px-4 py-1.5 rounded-full text-ti3 font-bold shadow-sm border ${
                        language === 'en'
                            ? 'bg-blue-50 text-blue-600 border-blue-200'
                            : 'bg-purple-50 text-purple-600 border-purple-200'
                    }`}>
                    {language === 'en' ? '🌎 ENGLISH (EN) MODE' : '🇰🇷 KOREAN (KO) MODE'}
                </span>
                <div className="h-4 w-px bg-line02 mx-2" />
                <Text className="text-ti3 text-font03 uppercase tracking-wider">Promotion Settings</Text>
            </div>

            {/* Compact Grid Layout */}
            <div className="grid grid-cols-12 gap-4">
                {/* Row 1: Name (6) + Priority (2) + Save Button (4, aligned right) */}
                <div className="col-span-8">
                    <Text className="text-ti2 text-font03 mb-1">Promotion Name</Text>
                    <UnderLineInput
                        value={name}
                        placeholder="Enter promotion name"
                        onChanged={text => dispatch({type: 'field_update', field: 'name', value: text})}
                        className="h-9 text-body3"
                    />
                </div>
                <div className="col-span-4">
                    <Text className="text-ti2 text-font03 mb-1">Priority</Text>
                    <UnderLineInput
                        value={priority}
                        type="number"
                        onChanged={text => dispatch({type: 'field_update', field: 'priority', value: text})}
                        className="h-9 text-body3"
                    />
                </div>

                {/* Row 2: Dates */}
                <div className="col-span-6 sm:col-span-12">
                    <Text className="text-ti2 text-font03 mb-1">Language</Text>
                    <WizSelectButton
                        data={languageList}
                        activeValue={languageList.find(l => l.id === language) || languageList[0]}
                        onChange={v => dispatch({type: 'field_update', field: 'language', value: v.id})}
                        className="w-full h-9"
                    />
                </div>
                <div className="col-span-6 sm:col-span-12">
                    <Text className="text-ti2 text-font03 mb-1">Start Date (KST)</Text>
                    <DateInput
                        value={start_at}
                        onChange={e => dispatch({type: 'field_update', field: 'start_at', value: e.target.value})}
                        className="h-9 text-body3 w-full"
                    />
                </div>
                <div className="col-span-6 sm:col-span-12">
                    <Text className="text-ti2 text-font03 mb-1">End Date (KST)</Text>
                    <DateInput
                        value={end_at}
                        onChange={e => dispatch({type: 'field_update', field: 'end_at', value: e.target.value})}
                        className="h-9 text-body3 w-full"
                    />
                </div>

                {/* Row 3: URLs */}
                <div className="col-span-6">
                    <Text className="text-ti2 text-font03 mb-1">PC Landing URL</Text>
                    <UnderLineInput
                        value={pc_url}
                        placeholder="https://..."
                        onChanged={text => dispatch({type: 'field_update', field: 'pc_url', value: text})}
                        className="h-9 text-body3"
                    />
                </div>
                <div className="col-span-6">
                    <Text className="text-ti2 text-font03 mb-1">Mobile Landing URL</Text>
                    <UnderLineInput
                        value={mo_url}
                        placeholder="https://..."
                        onChanged={text => dispatch({type: 'field_update', field: 'mo_url', value: text})}
                        className="h-9 text-body3"
                    />
                </div>

                {/* Footer Action */}
                <div className="col-span-12 flex justify-end gap-2 pt-4 mt-2 border-t border-line01">
                    {viewMode === 'edit' && (
                        <WizButton
                            text={'Delete Promotion'}
                            type={'red'}
                            onClick={handleDelete}
                            className="h-9 px-6 text-body3"
                        />
                    )}
                    <WizButton
                        text={viewMode === 'edit' ? 'Save Changes' : 'Create Promotion'}
                        type={'primary'}
                        onClick={handleSubmit}
                        className="h-9 px-6 text-body3"
                    />
                </div>
            </div>
        </div>
    )
}
